import React from 'react';
import { Overflow, OverflowItem, Tooltip, Button } from '@fluentui/react-components';
import { isCapabilityEnabled } from 'owa-capabilities';
import { headerFullBleedCapability } from 'owa-capabilities-definitions/lib/headerFullBleedCapability';
import classNames from 'owa-classnames';
import { isDrawerPaneVisible } from 'owa-drawer';
import { HEADER_BUTTONS_REGION_ID } from 'owa-header-constants';
import type { OwaHeaderButtonControl, OwaHeaderButtonControls } from 'owa-header-types';
import { observer } from 'owa-mobx-react';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import useButtonGroupContainerStyle from '../../hooks/useButtonGroupContainerStyle';
import useHeaderButtonVisibility from '../../hooks/useHeaderButtonVisibility';
import toggleHeaderPane from '../../orchestration/toggleHeaderPane';
import logHeaderButtonClick from '../../utils/logHeaderButtonClick';
import OwaHeaderButtonBadge from './OwaHeaderButtonBadge';
import OwaHeaderOverflowMenu from './OwaHeaderOverflowMenu';
import {
    activeButton,
    buttonBadge,
    buttonFullBleed,
    buttonIcon,
    headerButton,
    headerButtonGroup,
    overflowContainer,
} from './OwaHeaderButtonGroup.scss';

interface OwaHeaderButtonGroupProps {
    buttons: OwaHeaderButtonControls;
}

const defaultExport: React.FunctionComponent<OwaHeaderButtonGroupProps> = observer(
    function OwaHeaderButtonGroup({ buttons }) {
        // Account-scoped buttons (e.g. the consumer-only Rewards button) read the module-context
        // mailbox inside their `isVisible()`. Because this component is an `observer` and
        // useHeaderButtonVisibility evaluates visibility via a MobX `computed`, those buttons toggle
        // on an in-app account switch without a reload — no explicit account dependency needed.
        const visibleButtons = useHeaderButtonVisibility(buttons);
        const reservedVisibleButtonCount = React.useMemo(
            () => visibleButtons.filter(button => button.overflowItem?.addsHeaderSlot).length,
            [visibleButtons]
        );
        const containerStyle = useButtonGroupContainerStyle(
            visibleButtons.length,
            reservedVisibleButtonCount
        );

        return (
            <div style={containerStyle} className={headerButtonGroup}>
                <Overflow overflowDirection="start">
                    <div id={HEADER_BUTTONS_REGION_ID} className={overflowContainer}>
                        {visibleButtons.map(button => (
                            <OwaHeaderButton key={button.id} {...button} />
                        ))}
                        <OwaHeaderOverflowMenu buttons={visibleButtons} />
                    </div>
                </Overflow>
            </div>
        );
    },
    'OwaHeaderButtonGroup'
);

const OwaHeaderButton: React.FC<OwaHeaderButtonControl> = observer(
    ({ id, renderLabel, renderIcon, renderTooltip, onClick, pane, badge, overflowItem }) => {
        const handleClick = React.useCallback(() => {
            logHeaderButtonClick(id, 'header');
            onClick?.();
            if (pane) {
                toggleHeaderPane(pane.id);
            }
        }, [id, pane, onClick]);

        const icon = useComputedValue(() => {
            return (
                <div className={buttonIcon}>
                    {renderIcon()}
                    {badge?.getCount && (
                        <OwaHeaderButtonBadge
                            id={id}
                            getCount={badge.getCount}
                            className={buttonBadge}
                        />
                    )}
                </div>
            );
        }, [renderIcon, id, badge?.getCount]);

        const isActive = useComputedValue(() => {
            return pane ? isDrawerPaneVisible(pane.id) : false;
        }, [pane?.id]);

        const tooltip = renderTooltip ? renderTooltip() : renderLabel();
        const priority = overflowItem?.priority ?? 0;

        return (
            <>
                <OverflowItem key={id} id={id} priority={priority}>
                    <Tooltip content={tooltip} relationship="description">
                        <Button
                            id={id}
                            aria-label={tooltip}
                            aria-expanded={pane ? isActive : undefined}
                            className={classNames(
                                headerButton,
                                isCapabilityEnabled(headerFullBleedCapability) && buttonFullBleed,
                                isActive && activeButton
                            )}
                            icon={icon}
                            onClick={handleClick}
                        />
                    </Tooltip>
                </OverflowItem>
                {pane?.render && pane.render(pane.id)}
            </>
        );
    },
    'OwaHeaderButton'
);

export default defaultExport;
